import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable max-len */
import React, { useEffect, useMemo, useState, useRef, } from 'react';
import { observer } from 'mobx-react-lite';
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography, } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { replaceStylesheetGlobals } from '../portal/theme/portalTheme';
import StreamingMessages from './StreamingMessages';
// import ItemCarousel from '../item/ItemCarousel';
import ChatInput from './ChatInput';
import DismissableMessage from './DismissableMessage';
import ConversationWelcomeScreen from './ConversationWelcomeScreen';
import { useConversationStore } from '../ConversationStoreProvider';
import { useTokens } from '../tokens/TokensProvider';
import ItemCarousel from './ItemCarousel';
import SelectionWizardComposerArea from './wizard/SelectionWizardComposerArea';
import { mergeWizardLabelsIntoPromptList } from './wizard/wizardData';
import useWindowWidth from './hooks/useWindowWidth';
import { useIsMobileViewContext } from './IsMobileViewContext';
import usePageAdditionalDataRequest from './usePageAdditionalDataRequest';
import { DEFAULT_MAX_PAGE_WIDTH, } from './constants/dualModeConstants';
import { resolveLocalizedText, resolveLocalizedTextArray, } from '../utils/localization';
// eslint-disable-next-line react/require-default-props
export const SystemPrompt = ({ systemPrompt, modelID = undefined }) => {
    const [expanded, setExpanded] = useState(false);
    return (_jsxs(Accordion, { expanded: expanded, onChange: () => setExpanded(!expanded), sx: {
            bgcolor: 'grey.100',
            borderRadius: 1,
            '&:before': {
                display: 'none',
            },
            boxShadow: 'none',
        }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, {}), sx: {
                    minHeight: 48,
                    '&.Mui-expanded': {
                        minHeight: 48,
                    },
                }, children: _jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", sx: { width: '100%' }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 500 }, children: "System Prompt" }), modelID && (_jsxs(Typography, { variant: "caption", sx: { color: 'text.secondary', ml: 'auto' }, children: ["Model:", ' ', modelID] }))] }) }), _jsx(AccordionDetails, { sx: { pt: 0 }, children: _jsx(Box, { sx: {
                        p: 2,
                        borderRadius: 1,
                    }, children: _jsx("code", { style: { whiteSpace: 'pre-line' }, children: systemPrompt }) }) })] }));
};
const Conversation = ({ limitToPageWidth, showItemReferencesWithMessage = true, showItemReferencesCarousel = false, showResetWithInput, courseCardShowImage, courseCardShowTitle, courseCardShowInstructor, courseCardShowDescLines, animatedProcessingImage, openWebPagesInNewTab, style, feedbackButton, questionStyle, responseStyle, questionAlignment, responseAlignment, referenceFormatting, inputStyle, startingMessageFontStyle, enableVoiceInput, sendImage, resetConversationLabel = 'New Conversation', agentAvatarImage, userAvatarImage, conversationConnect, hideRightRailOnMobile, maxItemsInRightRail, enableMultiRefinements, multiRefinementSubmitButtonText, adminOptions, productFormatting, defaultProcessingIcon, productPromptHeader, onProductClick, onPortalRelativeNavigate, topSpaceReserved = '0px', itemReferenceDisplay, showPersistentPrompts = true, messagesContainerRef, streamingMessagesScrollRef, conversationContainerRef: conversationContainerRefProp, hideFirstMessage = false, disableInternalScroll = false, disableAutoScroll = false, renderCollapsed, noScroll = false, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
collapsedMaxHeight, maxPageWidth, disableArrowScroll, disableAddSpaceAnimation, onInputHeightChange, messageStackMode, }) => {
    const store = useConversationStore();
    const tokens = useTokens();
    // Get locale information for L10N resolution
    const locale = store.customerContext?.currentPage?.locale
        || store.customerContext?.locale;
    const defaultLocale = store.defaultLocale || 'en';
    const getTokenValue = (key) => {
        const tokenValue = tokens.getValue(key);
        if (!tokenValue || tokenValue.trim() === '' || tokenValue === `{{${key}}}`) {
            return '';
        }
        return tokenValue;
    };
    const resolvePortalText = (portalValue, tokenKey) => {
        if (portalValue && portalValue.trim()) {
            return portalValue;
        }
        return getTokenValue(tokenKey);
    };
    const resolvePortalPrompts = (portalValue, tokenKey) => {
        if (Array.isArray(portalValue) && portalValue.length > 0) {
            return portalValue;
        }
        if (typeof portalValue === 'string' && portalValue.trim()) {
            return portalValue;
        }
        const tokenValue = getTokenValue(tokenKey);
        if (tokenValue) {
            return tokenValue;
        }
        return [];
    };
    const { stylesheet } = store;
    const conversation = store.currentConversation;
    // Use provided ref or create local one
    const conversationContainerRefLocal = useRef(null);
    const conversationContainerRef = conversationContainerRefProp || conversationContainerRefLocal;
    // const {
    //   stylesheet,
    //   clientAIFeedbackButtons,
    // } = store.currentPortal;
    // const { isAIServiceUnavailable } = store.currentOrganization;
    const { /* stylesheet, */ clientAIFeedbackButtons, isAIServiceUnavailable } = store;
    const [isErrorDismissed, setErrorDismissed] = useState(false);
    const isMobileView = useIsMobileViewContext();
    const windowWidth = useWindowWidth();
    // Show welcome product/category prompts only until the shopper starts interacting.
    const [showWelcomePagePrompts, setShowWelcomePagePrompts] = useState(true);
    // Auto-scroll when PostPurchaseFlow appears (disabled when disableAutoScroll is true)
    useEffect(() => {
        if (disableAutoScroll) {
            // When auto-scroll is disabled, keep scroll at top
            return;
        }
        if (conversation?.postPurchaseFlow?.isVisible && conversationContainerRef.current) {
            // Scroll to bottom with smooth animation (consistent with StreamingMessages)
            setTimeout(() => {
                conversationContainerRef.current?.scrollTo({
                    top: conversationContainerRef.current.scrollHeight,
                    behavior: 'smooth',
                });
            }, 100); // Small delay to ensure PostPurchaseFlow is rendered
        }
    }, [conversation?.postPurchaseFlow?.isVisible, disableAutoScroll, conversationContainerRef]);
    const { append, error, handleInputChange, handleSubmit: baseHandleSubmit, input, isLoading, reset, saveFeedback, suggestedPrompts, handleSuggestedPromptClick: baseHandleSuggestedPromptClick, submitSelectionWizardToolResult, chatMessagesLength = 0, selectionWizardInMainColumn = false, wizardPlacementMessages = [], } = conversationConnect;
    // Wrap handleSuggestedPromptClick to hide prompts after click
    const handleSuggestedPromptClick = (prompt, type = 'product', source = 'host_page') => {
        setShowWelcomePagePrompts(false);
        baseHandleSuggestedPromptClick(prompt, type, source);
    };
    // Wrap handleSubmit to hide prompts after user submits any message
    const handleSubmit = (...args) => {
        setShowWelcomePagePrompts(false);
        baseHandleSubmit(...args);
    };
    const messageConfig = useMemo(() => {
        // const { clientAIConfig } = store.currentPortal;
        const { clientAIConfig } = store;
        const config = {
            showItemReferences: showItemReferencesWithMessage,
            showFeedbackButtons: clientAIFeedbackButtons,
            openWebPagesInNewTab,
            onPortalRelativeNavigate,
            questionStyle,
            responseStyle,
            questionAlignment,
            responseAlignment,
            agentAvatarImageAsset: agentAvatarImage,
            userAvatarImageAsset: userAvatarImage,
            agentAvatarImage,
            userAvatarImage,
            hideRightRailOnMobile,
            maxItemsInRightRail,
            enableMultiRefinements,
            multiRefinementSubmitButtonText,
            feedbackButton,
            openSupportToolDelay: clientAIConfig?.openSupportToolMessageDelay || 6000,
            openSupportToolMessage: tokens.resolvePortalText(clientAIConfig?.openSupportToolMessage, 'openSupportToolMessage'),
            defaultProcessingIcon,
            itemReferenceDisplay,
            hideProductAnswerTitleInMessage: clientAIConfig?.hideProductAnswerTitleInMessage ?? false,
            // Calculate dualModeEnabled from semantic props (for internal components like Message)
            // disableAutoScroll is true when in dual mode, so we can derive dualModeEnabled from it
            dualModeEnabled: disableAutoScroll,
        };
        return config;
    }, [
        showItemReferencesWithMessage,
        clientAIFeedbackButtons,
        questionStyle,
        responseStyle,
        questionAlignment,
        responseAlignment,
        agentAvatarImage,
        userAvatarImage,
        defaultProcessingIcon,
        openWebPagesInNewTab,
        onPortalRelativeNavigate,
        hideRightRailOnMobile,
        maxItemsInRightRail,
        enableMultiRefinements,
        feedbackButton,
        multiRefinementSubmitButtonText,
        store,
        itemReferenceDisplay,
        disableAutoScroll,
        tokens,
    ]);
    const mediaCardConfig = useMemo(() => ({
        courseCardShowPrice: /* courseCardShowPrice || */ false,
        courseCardShowTitle: courseCardShowTitle || false,
        courseCardShowInstructor: courseCardShowInstructor || false,
        courseCardShowImage: courseCardShowImage || false,
        courseCardShowDesc: (courseCardShowDescLines ?? 0) > 0,
        courseCardMaxLinesDescription: (courseCardShowDescLines || 2),
        courseCardShowInfoBar: false,
        courseCardShowActionButton: true,
        referenceFormatting,
        openWebPagesInNewTab,
        productFormatting,
        clientAIConfig: store.clientAIConfig,
    }), [
        courseCardShowImage,
        courseCardShowTitle,
        courseCardShowInstructor,
        courseCardShowDescLines,
        referenceFormatting,
        openWebPagesInNewTab,
        productFormatting,
        store.clientAIConfig,
    ]);
    // Request page-specific content whenever host page identity/config becomes available or changes.
    // The store owns in-flight/success deduplication, so the dispatcher requesting the same page
    // before the assistant is expanded makes this a no-op.
    const { isProductPage, isCollectionPage } = usePageAdditionalDataRequest(store);
    // Resolve suggested prompts with L10N support
    const localizedSuggestedPrompts = resolveLocalizedTextArray({
        legacyValue: store.clientAISuggestedPrompts,
        localizedMap: store.clientAISuggestedPromptsL10N,
        enabled: store.clientAISuggestedPromptsLocalized,
        locale,
        defaultLocale,
    });
    const promptsRaw = resolvePortalPrompts(localizedSuggestedPrompts, 'suggestedPrompts');
    let parsedPrompts;
    if (Array.isArray(promptsRaw)) {
        parsedPrompts = promptsRaw;
    }
    else if (typeof promptsRaw === 'string') {
        try {
            const parsed = JSON.parse(promptsRaw);
            parsedPrompts = Array.isArray(parsed) ? parsed : [parsed];
        }
        catch (err) {
            parsedPrompts = [promptsRaw];
            // console.log('[Conversation] Could not parse promptsRaw as JSON. Using raw string.');
        }
    }
    else {
        parsedPrompts = [];
    }
    const resolvedSuggestedPrompts = parsedPrompts.join('&#8226;');
    // Resolve persistent prompts with L10N support
    const localizedPersistentPrompts = resolveLocalizedText({
        legacyValue: store.clientAIPersistentPrompts,
        localizedMap: store.clientAIPersistentPromptsL10N,
        enabled: store.clientAIPersistentPromptsLocalized,
        locale,
        defaultLocale,
    });
    const resolvedPersistentPrompts = resolvePortalText(localizedPersistentPrompts, 'persistentPrompts');
    const isInlineEmbedMode = useMemo(() => {
        if (store.aiMode === 'inline') {
            return true;
        }
        if (typeof window === 'undefined') {
            return false;
        }
        return new URLSearchParams(window.location.search).get('inline') === '1';
    }, [store.aiMode]);
    const shouldSuppressPersistentPrompts = showPersistentPrompts === false;
    const finalPersistentPrompts = shouldSuppressPersistentPrompts
        ? ''
        : mergeWizardLabelsIntoPromptList(resolvedPersistentPrompts, store.clientAIConfig);
    const suppressSuggestedPromptMessage = isInlineEmbedMode
        && store.clientAIConfig?.suppressSuggestedPromptMessage === true;
    const resolvedStartingMessage = resolvePortalText(store.clientAIStartingMessage, 'startingMessage');
    const resolvedStartingMessageForWelcome = suppressSuggestedPromptMessage
        ? ''
        : resolvedStartingMessage;
    const showErrorMessage = !isLoading && (isAIServiceUnavailable || error) && !isErrorDismissed;
    // Track if prompts were rendered in the page (embed) or should be rendered in chat
    const [promptsRenderedInPage, setPromptsRenderedInPage] = useState(false);
    // Listen for messages from parent window about prompt rendering
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data?.source !== 'microcommerce-ai-embed-parent') {
                return;
            }
            if (event.data.type === 'suggestedPromptsRenderedInPage' ||
                event.data.type === 'productAnswersRenderedInPage' ||
                event.data.type === 'categoryAnswersRenderedInPage' ||
                event.data.type === 'smartSummaryRenderedInPage') {
                if (!isInlineEmbedMode) {
                    setPromptsRenderedInPage(true);
                }
            }
            else if (event.data.type === 'suggestedPromptsContainerNotFound' ||
                event.data.type === 'productAnswersContainerNotFound' ||
                event.data.type === 'categoryAnswersContainerNotFound' ||
                event.data.type === 'smartSummaryContainerNotFound') {
                setPromptsRenderedInPage(false);
            }
        };
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [isInlineEmbedMode]);
    // Determine which prompts to show
    const showProductPromptsInShoppingAssistant = store.clientAIConfig?.showProductPromptsInShoppingAssistant ?? true;
    const displayProductQuestionsAtConversationEnd = store.clientAIConfig?.displayProductQuestionsAtConversationEnd ?? true;
    const promptsHiddenByExternalRender = !isInlineEmbedMode && promptsRenderedInPage;
    // Only show in chat if not rendered in page
    const shouldShowProductPrompts = isProductPage
        && showWelcomePagePrompts
        && suggestedPrompts.length > 0
        && showProductPromptsInShoppingAssistant
        && !promptsHiddenByExternalRender;
    const shouldShowCategoryPrompts = isCollectionPage
        && showWelcomePagePrompts
        && suggestedPrompts.length > 0
        && showProductPromptsInShoppingAssistant
        && !promptsHiddenByExternalRender;
    const shouldShowProductPromptsAtConversationEnd = isProductPage
        && suggestedPrompts.length > 0
        && displayProductQuestionsAtConversationEnd;
    const shouldShowInitialPrompts = ((!isProductPage && !isCollectionPage) || (!shouldShowProductPrompts && !shouldShowCategoryPrompts)) && resolvedSuggestedPrompts;
    const suggestedPromptsChipsForWelcome = typeof shouldShowInitialPrompts === 'string'
        ? shouldShowInitialPrompts
        : '';
    const suggestedPromptsChipsForWelcomeFinal = suppressSuggestedPromptMessage
        ? ''
        : suggestedPromptsChipsForWelcome;
    // Determine headers dynamically based on page type
    const categoryPromptHeader = tokens.resolvePortalText(store.clientAIConfig?.categoryPageSuggestedPromptsHeader, 'categoryPromptHeader') || tokens.resolvePortalText(store.clientAIConfig?.pageSuggestedPromptsHeader, 'pageSuggestedPromptsHeader') || tokens.getValue('categoryPromptHeader');
    const resolvedProductPromptHeader = productPromptHeader
        || tokens.resolvePortalText(store.clientAIConfig?.pageSuggestedPromptsHeader, 'pageSuggestedPromptsHeader')
        || tokens.getValue('productPromptHeader');
    const willEnterDualLayout = !!(messagesContainerRef && streamingMessagesScrollRef && conversationContainerRef);
    const resolvedMessageStackMode = messageStackMode || (willEnterDualLayout ? 'natural' : 'fill');
    // Ref to measure actual input bar height
    const inputBarRef = React.useRef(null);
    const lastReportedInputHeight = React.useRef(null);
    // Measure input bar height when in dual mode
    useEffect(() => {
        if (!willEnterDualLayout) {
            if (lastReportedInputHeight.current !== null) {
                lastReportedInputHeight.current = null;
                onInputHeightChange?.(null);
            }
            return undefined;
        }
        const measureInput = () => {
            const inputEl = inputBarRef.current;
            if (!inputEl)
                return;
            const height = Math.max(inputEl.scrollHeight || 0, inputEl.offsetHeight || 0, inputEl.clientHeight || 0);
            if (height > 0 && height !== lastReportedInputHeight.current) {
                lastReportedInputHeight.current = height;
                onInputHeightChange?.(height);
            }
        };
        measureInput();
        const resizeObserver = new ResizeObserver(measureInput);
        if (inputBarRef.current) {
            resizeObserver.observe(inputBarRef.current);
        }
        return () => {
            resizeObserver.disconnect();
        };
    }, [willEnterDualLayout, onInputHeightChange]);
    if (!conversation) {
        return null;
    }
    /**
     * Placement + "Edit wizard" read from useChat when present; else MobX (e.g. before hydration).
     * Must match `selectionWizardInMainColumn`, which is computed only from useChat in useConversation.
     */
    const messagesForWizardComposer = wizardPlacementMessages.length > 0
        ? wizardPlacementMessages
        : conversation.messages.slice();
    /** Prefer useChat length too — after tools (e.g. selection wizard) the store can lag one frame behind useChat. */
    const hasThreadForUi = conversation.hasMessages() || chatMessagesLength > 0;
    const chatInputEl = (_jsx(ChatInput, { input: input, enableVoiceInput: Boolean(enableVoiceInput), handleInputChange: handleInputChange, handleSubmit: handleSubmit, isLoading: isLoading, append: append, persistentPrompts: finalPersistentPrompts, sendImage: sendImage, style: inputStyle, reset: showResetWithInput ? reset : undefined, resetConversationLabel: resetConversationLabel }));
    const selectionWizardComposerArea = (_jsx(SelectionWizardComposerArea, { fillsConversationArea: selectionWizardInMainColumn, messages: messagesForWizardComposer, append: append, submitSelectionWizardToolResult: submitSelectionWizardToolResult, chatInput: chatInputEl }));
    if (willEnterDualLayout) {
        return (_jsxs(Stack, { ref: conversationContainerRef, direction: "column", sx: {
                height: 'auto',
                minHeight: 0,
                display: 'flex',
                ...(renderCollapsed && {
                    overflow: 'hidden',
                }),
                border: replaceStylesheetGlobals(stylesheet, style.borderColor),
                borderWidth: style.borderWidth,
                borderRadius: style.borderRadius,
                borderStyle: 'solid',
                backgroundColor: replaceStylesheetGlobals(stylesheet, style.backgroundColor),
                color: replaceStylesheetGlobals(stylesheet, style.color),
                ...(limitToPageWidth && {
                    width: '100%',
                    maxWidth: maxPageWidth || DEFAULT_MAX_PAGE_WIDTH,
                    marginX: 'auto',
                }),
            }, children: [adminOptions?.returnSystemPrompt && (_jsx(SystemPrompt, { systemPrompt: conversation.systemPrompt })), _jsxs(Box, { ref: (el) => {
                        if (messagesContainerRef && el) {
                            messagesContainerRef.current = el;
                        }
                        else if (messagesContainerRef && !el) {
                            messagesContainerRef.current = null;
                        }
                    }, sx: {
                        flex: selectionWizardInMainColumn
                            ? 1
                            : (resolvedMessageStackMode === 'natural' ? '0 1 auto' : 1),
                        display: 'flex',
                        flexDirection: 'column',
                        ...(renderCollapsed && {
                            overflow: 'hidden',
                            ...(collapsedMaxHeight && collapsedMaxHeight !== 'none' ? {
                                maxHeight: collapsedMaxHeight,
                            } : {}),
                        }),
                        minHeight: 0, // Important for flex children to respect overflow
                    }, children: [selectionWizardInMainColumn ? (selectionWizardComposerArea) : (_jsx(_Fragment, { children: hasThreadForUi ? (_jsx(StreamingMessages, { messageConfig: messageConfig, mediaCardConfig: mediaCardConfig, isLoading: isLoading, saveFeedback: saveFeedback, isMobileView: isMobileView, animatedProcessingImage: animatedProcessingImage, productPrompts: suggestedPrompts, productPromptHeader: resolvedProductPromptHeader, onProductPromptClick: (prompt) => handleSuggestedPromptClick(prompt, 'product', 'shopping_assistant'), showProductPrompts: shouldShowProductPromptsAtConversationEnd, onProductClick: onProductClick, hideRightRailOnMobile: hideRightRailOnMobile, maxItemsInRightRail: maxItemsInRightRail, productFormatting: productFormatting, topSpaceReserved: topSpaceReserved, disableAutoScroll: disableAutoScroll, streamingMessagesScrollRef: streamingMessagesScrollRef, hideFirstMessage: hideFirstMessage, disableInternalScroll: disableInternalScroll, hideScrollDownIcon: disableArrowScroll || disableInternalScroll, disableAddSpaceAnimation: disableAddSpaceAnimation || disableInternalScroll, messageStackMode: resolvedMessageStackMode, chatMessagesLength: chatMessagesLength })) : (_jsx(ConversationWelcomeScreen, { visible: !hasThreadForUi, customHtml: !suppressSuggestedPromptMessage && !shouldShowProductPrompts && !shouldShowCategoryPrompts
                                    ? store.clientAIConfig?.welcomeScreenHtml : undefined, startingMessage: resolvedStartingMessageForWelcome, startingMessageFontStyle: startingMessageFontStyle, shouldShowProductPrompts: shouldShowProductPrompts, shouldShowCategoryPrompts: shouldShowCategoryPrompts, suggestedPromptsChips: suggestedPromptsChipsForWelcomeFinal, suggestedPrompts: suggestedPrompts, resolvedProductPromptHeader: resolvedProductPromptHeader, categoryPromptHeader: categoryPromptHeader, isLoading: isLoading, append: append, onProductPromptClick: (prompt, type) => handleSuggestedPromptClick(prompt, type ?? 'product', 'shopping_assistant') })) })), showErrorMessage && (_jsx(DismissableMessage, { message: tokens.getValue('serviceUnavailableMessage'), onClose: () => setErrorDismissed(true) }))] }), _jsxs(Box, { ref: inputBarRef, sx: {
                        // Keep composer + wizard from collapsing to 0 when the message stack is tall (flexShrink: 1 hid the footer).
                        flexShrink: 0,
                        minWidth: 0,
                        ...(renderCollapsed && {
                            position: 'sticky',
                            bottom: 0,
                            backgroundColor: replaceStylesheetGlobals(stylesheet, style.backgroundColor) || '#ffffff',
                            zIndex: 100,
                        }),
                    }, children: [!selectionWizardInMainColumn && selectionWizardComposerArea, showItemReferencesCarousel && conversation.carouselItems.length > 0 && (_jsx(ItemCarousel, { items: Object.values(conversation.carouselItems), backgroundColor: "inherit", showPrice: false, cardShowImage: courseCardShowImage, cardShowTitle: courseCardShowTitle, cardShowInstructor: courseCardShowInstructor, cardShowDescription: !!(courseCardShowDescLines && courseCardShowDescLines > 0), cardMaxLinesDescription: courseCardShowDescLines || 2, cardShowInfoBar: false, limitToPageWidth: true, onProductClick: onProductClick, onPortalRelativeNavigate: onPortalRelativeNavigate, openInNewTab: openWebPagesInNewTab }))] })] }));
    }
    // Original layout for non-dual mode
    return (_jsxs(Stack, { ref: conversationContainerRef, direction: "column", sx: {
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            border: replaceStylesheetGlobals(stylesheet, style.borderColor),
            borderWidth: style.borderWidth,
            borderRadius: style.borderRadius,
            borderStyle: 'solid',
            backgroundColor: replaceStylesheetGlobals(stylesheet, style.backgroundColor),
            color: replaceStylesheetGlobals(stylesheet, style.color),
            ...(limitToPageWidth && {
                width: '100%',
                maxWidth: maxPageWidth || DEFAULT_MAX_PAGE_WIDTH,
                marginX: 'auto',
            }),
            // flex-start so the messages region can shrink below content when the wizard/composer is tall
            justifyContent: 'flex-start',
            alignItems: 'stretch',
        }, children: [adminOptions?.returnSystemPrompt && (_jsx(SystemPrompt, { systemPrompt: conversation.systemPrompt, modelID: conversation.modelID })), _jsxs(Box, { sx: {
                    flex: selectionWizardInMainColumn ? 1 : '1 1 0%',
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }, children: [selectionWizardInMainColumn ? (selectionWizardComposerArea) : (_jsx(_Fragment, { children: hasThreadForUi ? (_jsx(StreamingMessages, { messageConfig: messageConfig, mediaCardConfig: mediaCardConfig, isLoading: isLoading, saveFeedback: saveFeedback, isMobileView: isMobileView, animatedProcessingImage: animatedProcessingImage, productPrompts: suggestedPrompts, productPromptHeader: resolvedProductPromptHeader, onProductPromptClick: (prompt) => handleSuggestedPromptClick(prompt, 'product', 'shopping_assistant'), showProductPrompts: shouldShowProductPromptsAtConversationEnd, onProductClick: onProductClick, hideRightRailOnMobile: hideRightRailOnMobile, maxItemsInRightRail: maxItemsInRightRail, productFormatting: productFormatting, topSpaceReserved: topSpaceReserved, disableAutoScroll: disableAutoScroll, streamingMessagesScrollRef: streamingMessagesScrollRef, hideFirstMessage: hideFirstMessage, disableInternalScroll: disableInternalScroll, hideScrollDownIcon: disableArrowScroll || disableInternalScroll, disableAddSpaceAnimation: disableAddSpaceAnimation || disableInternalScroll, messageStackMode: resolvedMessageStackMode, chatMessagesLength: chatMessagesLength })) : (_jsx(ConversationWelcomeScreen, { visible: !hasThreadForUi, customHtml: !suppressSuggestedPromptMessage && !shouldShowProductPrompts && !shouldShowCategoryPrompts
                                ? store.clientAIConfig?.welcomeScreenHtml : undefined, startingMessage: resolvedStartingMessageForWelcome, startingMessageFontStyle: startingMessageFontStyle, shouldShowProductPrompts: shouldShowProductPrompts, shouldShowCategoryPrompts: shouldShowCategoryPrompts, suggestedPromptsChips: suggestedPromptsChipsForWelcomeFinal, suggestedPrompts: suggestedPrompts, resolvedProductPromptHeader: resolvedProductPromptHeader, categoryPromptHeader: categoryPromptHeader, isLoading: isLoading, append: append, onProductPromptClick: (prompt, type) => handleSuggestedPromptClick(prompt, type ?? 'product', 'shopping_assistant') })) })), showErrorMessage && (_jsx(DismissableMessage, { message: tokens.getValue('serviceUnavailableMessage'), onClose: () => setErrorDismissed(true) }))] }), _jsx(Box, { sx: {
                    flex: '0 1 auto',
                    minHeight: 0,
                    maxHeight: '100%',
                    minWidth: 0,
                    width: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }, children: !selectionWizardInMainColumn && selectionWizardComposerArea }), showItemReferencesCarousel && conversation.carouselItems.length > 0 && (_jsx(ItemCarousel, { items: Object.values(conversation.carouselItems), backgroundColor: "inherit", showPrice: false, cardShowImage: courseCardShowImage, cardShowTitle: courseCardShowTitle, cardShowInstructor: courseCardShowInstructor, cardShowDescription: !!(courseCardShowDescLines && courseCardShowDescLines > 0), cardMaxLinesDescription: courseCardShowDescLines || 2, cardShowInfoBar: false, limitToPageWidth: true, onProductClick: onProductClick, onPortalRelativeNavigate: onPortalRelativeNavigate, openInNewTab: openWebPagesInNewTab }))] }));
};
Conversation.getData = () => { };
Conversation.defaultProps = {
    adminOptions: undefined,
    limitToPageWidth: true,
    enableVoiceInput: false,
    showItemReferencesWithMessage: true,
    showItemReferencesCarousel: false,
    itemReferenceDisplay: 'card',
    messagesContainerRef: undefined,
    streamingMessagesScrollRef: undefined,
    conversationContainerRef: undefined,
    hideFirstMessage: false,
    disableInternalScroll: false,
    disableAutoScroll: false,
    showPersistentPrompts: true,
    renderCollapsed: false,
    noScroll: false,
    collapsedMaxHeight: 'none',
    maxPageWidth: DEFAULT_MAX_PAGE_WIDTH,
    disableArrowScroll: false,
    disableAddSpaceAnimation: false,
    showResetWithInput: true,
    onInputHeightChange: undefined,
    messageStackMode: undefined,
    style: {
        backgroundColor: 'rgba(0,0,0,0)',
        borderColor: 'color_primary',
        borderRadius: 2,
        borderWidth: 0,
        color: 'color_font',
        margin: 4,
        padding: 4,
        shadow: 0,
    },
    courseCardShowImage: true,
    courseCardShowTitle: true,
    courseCardShowInstructor: false,
    onProductClick: undefined,
    courseCardShowDescLines: 2,
    topSpaceReserved: '0px',
    animatedProcessingImage: undefined,
    enableMultiRefinements: false,
    multiRefinementSubmitButtonText: 'Submit Refinements',
    questionStyle: {
        backgroundColor: '#dcdcdc',
        borderColor: '#9b9b9b',
        borderRadius: 8,
        borderWidth: 1,
        color: 'color_font',
        margin: 0,
        padding: 12,
        shadow: 1,
    },
    responseStyle: {
        backgroundColor: 'color_background',
        borderColor: '#9b9b9b',
        borderRadius: 8,
        borderWidth: 1,
        color: 'color_font',
        margin: 0,
        padding: 12,
        shadow: 1,
    },
    feedbackButton: {
        color: '#000',
        hover: '#ccc',
    },
    questionAlignment: 'right-60',
    responseAlignment: 'left',
    referenceFormatting: undefined,
    inputStyle: {
        backgroundColor: 'rgba(0,0,0,0)',
        borderColor: 'color_primary',
        borderRadius: 2,
        borderWidth: 0,
        color: 'color_font',
        margin: 4,
        padding: 4,
        shadow: 0,
    },
    startingMessageFontStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: 'color_font',
    },
    productFormatting: {
        // template: 'standard',
        orientation: 'vertical',
        showBox: false,
        showImage: true,
        showTitle: true,
        showDescription: false,
        showPrice: false,
        borderStyle: {
            borderWidth: 1,
            borderColor: '#ccc',
            borderRadius: 4,
        },
        backgroundColor: '#fff',
    },
    sendImage: undefined,
    resetConversationLabel: 'New Conversation', // Will be overridden by tokens in component
    agentAvatarImage: undefined,
    userAvatarImage: undefined,
    defaultProcessingIcon: {
        color: 'color_primary',
        width: 80,
        height: 20,
    },
    productPromptHeader: undefined,
};
export const sharedConversationMeta = [
    {
        key: 'layoutMode',
        name: 'Layout Mode',
        type: 'select',
        values: ['ai', 'dual'],
        labels: ['AI Mode', 'Dual Mode'],
        description: 'Layout mode for the search component: AI Mode (normal) or Dual Mode (dual search mode with native results).',
        default: 'ai',
    },
    {
        key: 'enableVoiceInput',
        name: 'Enable Voice Input',
        type: 'boolean',
        description: 'Displays voice input button.',
    },
    {
        key: 'startingMessageFontStyle',
        name: 'Starting Message Font',
        type: 'fontStyle',
        description: 'Style of the starting message.',
        group: 'style',
    },
    {
        key: 'sendImage',
        name: 'Send Button Image',
        type: 'imageAsset',
        description: 'Image to show in Send Message text box.',
    },
    {
        key: 'showPersistentPrompts',
        name: 'Show Persistent Prompts',
        type: 'boolean',
        default: true,
        description: 'When enabled, persistent prompt buttons appear below the input.',
    },
    {
        key: 'showResetWithInput',
        name: 'Show Reset Below Input',
        type: 'boolean',
        description: 'If selected a reset button is included button the input box.',
    },
    {
        key: 'resetConversationLabel',
        name: 'Reset Conversation Label',
        type: 'text',
        description: 'Label for Reset Conversation button.',
    },
    {
        key: 'agentAvatarImage',
        name: 'Agent Avatar',
        type: 'imageAsset',
        description: 'Image to show next to Agent messages.',
    },
    {
        key: 'userAvatarImage',
        name: 'User Avatar',
        type: 'imageAsset',
        description: 'Image to show next to User messages.',
    },
    {
        key: 'questionStyle',
        name: 'Question Style',
        type: 'boxStyle',
        description: 'Style of user questions.',
        group: 'style',
    },
    {
        key: 'responseStyle',
        name: 'Response Style',
        type: 'boxStyle',
        description: 'Style of AI response.',
        group: 'style',
    },
    {
        key: 'feedbackButton.color',
        name: 'Feedback Button Color',
        type: 'color',
        description: 'Color of the thumbs up/down feedback buttons.',
        group: 'style',
    },
    {
        key: 'feedbackButton.hover',
        name: 'Feedback Button Color (Hover)',
        type: 'color',
        description: 'Color of the thumbs up/down feedback buttons on hover action.',
        group: 'style',
    },
    {
        key: 'defaultProcessingIcon.color',
        name: 'Default Processing Icon Color',
        type: 'color',
        description: 'Color of the animated processing icon.',
        group: 'style',
    },
    {
        key: 'defaultProcessingIcon.width',
        name: 'Default Processing Icon Width',
        type: 'number',
        description: 'Width of the animated processing icon.',
        group: 'style',
    },
    {
        key: 'defaultProcessingIcon.height',
        name: 'Default Processing Icon Height',
        type: 'number',
        description: 'Height of the animated processing icon.',
        group: 'style',
    },
    {
        key: 'questionAlignment',
        name: 'Question Alignment',
        label: 'Question Alignment',
        type: 'select',
        values: ['left', 'right', 'right-60'],
        labels: ['Left', 'Right', 'Right 60%'],
        description: 'Alignment for the question area.',
    },
    {
        key: 'responseAlignment',
        name: 'Response Alignment',
        label: 'Response Alignment',
        type: 'select',
        values: ['left', 'leftIndent', 'right'],
        labels: ['Left', 'Left Indent', 'Right'],
        description: 'Alignment for the response area.',
    },
    {
        key: 'referenceFormatting.outline',
        name: 'Reference Enable Outline',
        type: 'boolean',
        group: 'style',
        description: 'Enable or disable outline around references.',
    },
    {
        key: 'referenceFormatting.outlineType',
        name: 'Outline Type',
        label: 'Reference Outline Type',
        type: 'select',
        values: ['solid', 'dashed', 'dotted'],
        labels: ['Solid', 'Dashed', 'Dotted'],
        group: 'style',
        description: 'The style of outline.',
    },
    {
        key: 'referenceFormatting.outlineColor',
        name: 'Reference Outline Color',
        type: 'color',
        group: 'style',
        description: 'Outline color around references.',
    },
    {
        key: 'referenceFormatting.outlineWeight',
        name: 'Reference Outline Weight',
        type: 'number',
        min: 1,
        max: 10,
        group: 'style',
        description: 'The thickness of the outline.',
    },
    {
        key: 'referenceFormatting.backgroundColor',
        name: 'Reference Background Color',
        type: 'color',
        group: 'style',
        description: 'Background color of the references.',
    },
    {
        key: 'inputStyle',
        name: 'Message Input Style',
        type: 'boxStyle',
        description: 'Style of the input area of the chat.',
        group: 'style',
    },
    {
        key: 'courseCardShowImage',
        name: 'Show Image',
        type: 'boolean',
        description: 'Show or hide the image in the course card.',
    },
    {
        key: 'courseCardShowTitle',
        name: 'Show Title',
        type: 'boolean',
        description: 'Show or hide the title in the course card.',
    },
    {
        key: 'courseCardShowInstructor',
        name: 'Show Instructor',
        type: 'boolean',
        description: 'Show or hide the instructor in the course card.',
    },
    {
        key: 'courseCardShowDescLines',
        name: 'Number of Description Lines',
        type: 'number',
        description: 'Number of description lines to show in the course card.',
    },
    {
        key: 'openWebPagesInNewTab',
        name: 'Open Web Pages in New Tab',
        type: 'boolean',
        description: 'Open web pages in a new tab.',
    },
    {
        key: 'maxItemsInRightRail',
        name: 'Maximum Number of Items In Right Rail',
        type: 'number',
        description: 'The maximum number of items that will be displayed in the Right Rail.',
    },
    {
        key: 'hideRightRailOnMobile',
        name: 'Hide Right Rail On Mobile',
        type: 'boolean',
        description: 'Show or hide the right rail (carousel) on mobile devices.',
    },
    {
        key: 'clientAIAnimatedProcessingIcon',
        name: 'Animated Processing Icon',
        type: 'imageAsset',
        description: 'Image to show in Send Message text box.',
    },
    {
        key: 'enableMultiRefinements',
        name: 'Enable Multi-Refinements',
        type: 'boolean',
        description: 'Allow users to select multiple refinements before submitting.',
    },
    {
        key: 'multiRefinementSubmitButtonText',
        name: 'Multi-Refinement Submit Button Text',
        type: 'text',
        description: 'Text to display on the submit button for multi-refinements.',
        default: 'Submit Refinements',
    },
    // {
    //   key: 'productFormatting.template',
    //   name: 'Product Template',
    //   label: 'Product Card Template',
    //   type: 'select',
    //   values: ['minimalistic', 'standard', 'detailed', 'custom'],
    //   labels: ['Minimalistic', 'Standard', 'Detailed', 'Custom'],
    //   description: 'Predefined visual format for products.',
    //   group: 'style',
    // },
    {
        key: 'productFormatting.orientation',
        name: 'Product Card Orientation',
        label: 'Product Card Orientation',
        type: 'select',
        values: ['horizontal', 'vertical'],
        labels: ['Horizontal', 'Vertical'],
        description: 'Orientation layout for the product card.',
        group: 'style',
    },
    {
        key: 'productFormatting.showBox',
        name: 'Product Card Show Box',
        type: 'boolean',
        description: 'Show or hide box around the product.',
        group: 'style',
    },
    {
        key: 'productFormatting.showImage',
        name: 'Product Card Show Image',
        type: 'boolean',
        description: 'Show or hide image.',
        group: 'style',
    },
    {
        key: 'productFormatting.showTitle',
        name: 'Product Card Show Title',
        type: 'boolean',
        description: 'Show or hide title.',
        group: 'style',
    },
    {
        key: 'productFormatting.showDescription',
        name: 'Product Card Show Description',
        type: 'boolean',
        description: 'Show or hide description.',
        group: 'style',
    },
    {
        key: 'productFormatting.showPrice',
        name: 'Product Card Show Price',
        type: 'boolean',
        description: 'Show or hide price.',
        group: 'style',
    },
    {
        key: 'productFormatting.borderStyle.borderWidth',
        name: 'Product Card Border Width',
        type: 'number',
        min: 0,
        max: 10,
        description: 'Border width of product box.',
        group: 'style',
    },
    {
        key: 'productFormatting.borderStyle.borderColor',
        name: 'Product Card Border Color',
        type: 'color',
        description: 'Border color of product box.',
        group: 'style',
    },
    {
        key: 'productFormatting.borderStyle.borderRadius',
        name: 'Product Card Border Radius',
        type: 'number',
        min: 0,
        max: 20,
        description: 'Border radius of product box.',
        group: 'style',
    },
    {
        key: 'topSectionMaxHeightPx',
        name: 'Top Section Max Height - AI Mode (px)',
        type: 'number',
        description: 'Maximum height of the Top Section before showing "See More" button.',
        default: 400,
    },
    {
        key: 'seeMoreButtonLabel',
        name: 'See More Button Label',
        type: 'text',
        description: 'Text displayed on the "See More Results" button.',
        default: 'See more results',
    },
    {
        key: 'seeMoreBehavior',
        name: 'See More Button Behavior',
        label: 'See More Button Behavior',
        type: 'select',
        values: ['expand', 'navigate'],
        labels: ['Expand', 'Navigate'],
        description: 'Behavior when "See More Results" button is clicked.',
    },
    {
        key: 'productCardVariant',
        name: 'Product Card Variant',
        label: 'Product Card Variant',
        type: 'select',
        values: ['minimal', 'withPrice'],
        labels: ['Minimal', 'With Price'],
        description: 'Preset variant for product cards in Right Pane.',
    },
    {
        key: 'itemReferenceDisplay',
        name: 'Item Reference Display',
        type: 'select',
        values: ['card', 'link'],
        labels: ['Card', 'Link'],
        description: 'How to display item references in messages: Card (full card) or Link (title only).',
        default: 'card',
    },
    {
        key: 'hideFirstMessage',
        name: 'Hide First Message',
        type: 'boolean',
        description: 'Hide the first message in the conversation (used in dual search mode).',
        default: false,
    },
    {
        key: 'disableAutoScroll',
        name: 'Disable Auto Scroll (Use for Dual Search)',
        type: 'boolean',
        description: 'Disable automatic scrolling to bottom when new messages arrive (used in dual search mode). Defaults to true when Dual Mode is selected.',
        default: false,
    },
];
export default observer(Conversation);
//# sourceMappingURL=Conversation.js.map